import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { analysisKeys } from '../api/queryKeys';
import { analyticalDatasetResultsQueryOptions } from '../api/useAnalyticalDatasetResultsQuery';
import { analyticalVariableLookupQueryOptions } from '../api/useAnalyticalVariableLookupQuery';
import { buildInitialResultsSearchParams } from '../utils/datasetResultsGrid';
import type { ProcessingStage } from '../utils/processingStage';

// A prefetched variable lookup is reused by the grid and the pickers for this
// long before either refetches it.
const LOOKUP_PREFETCH_STALE_TIME_MS = 60_000;

interface UseAnalysisDataPrefetchArgs {
  projectId: string;
  /** The population's analytical dataset, once the definition has loaded. */
  analyticalDatasetProfileId: string | null;
  /** True once the analysis has a finished run whose results are current. */
  hasCurrentResults: boolean;
  /** The stage of the run in flight, or null when no run is processing. */
  processingStage: ProcessingStage | null;
}

/**
 * Starts the requests the analysis page's other views need as soon as their
 * inputs are known, so a view is already cached when the user opens it:
 *
 * - the analytical variable lookup (variable names for the dataset's column
 *   headers and rows, and the add-variable picker's options) on mount, in
 *   parallel with everything else on the page;
 * - the analytical dataset's first results page (its columns and first rows)
 *   once the definition has named the dataset and the run is current, ahead of
 *   the user choosing the "Analytical dataset" option.
 *
 * While a run processes, the dataset's rows are rebuilt in its first stage and
 * final once it moves to the second, so the cached pages are refetched at that
 * point rather than when the whole run ends.
 *
 * Prefetch failures are silent: the view that needs the data refetches it.
 */
export const useAnalysisDataPrefetch = ({
  projectId,
  analyticalDatasetProfileId,
  hasCurrentResults,
  processingStage,
}: UseAnalysisDataPrefetchArgs): void => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!projectId) return;
    void queryClient.prefetchQuery({
      ...analyticalVariableLookupQueryOptions(projectId),
      staleTime: LOOKUP_PREFETCH_STALE_TIME_MS,
    });
  }, [queryClient, projectId]);

  useEffect(() => {
    if (!projectId || !analyticalDatasetProfileId || !hasCurrentResults) return;
    void queryClient.prefetchQuery(
      analyticalDatasetResultsQueryOptions({
        projectId,
        analyticalDatasetProfileId,
        searchParams: buildInitialResultsSearchParams(),
      })
    );
  }, [queryClient, projectId, analyticalDatasetProfileId, hasCurrentResults]);

  // The dataset's rows are final once the run reaches its second stage. Refetch
  // every cached page then (including ones nothing is showing), so the grid opens
  // on the new rows instead of waiting for the run to finish. Only a transition
  // seen from the first stage counts: a page opened mid-run reads fresh data anyway.
  const previousStage = useRef<ProcessingStage | null>(processingStage);
  useEffect(() => {
    if (previousStage.current === 'DATASETS' && processingStage === 'CELLS') {
      void queryClient.invalidateQueries(
        { queryKey: analysisKeys.datasetResultsAll(), refetchType: 'all' },
        { cancelRefetch: false }
      );
    }
    previousStage.current = processingStage;
  }, [queryClient, processingStage]);
};
